import { useState } from 'react'

import {
  useAcceptVulnerability,
  useVulnSummary,
  useVulnerabilities,
  type VulnRow,
} from '../api/queries'
import { useTenant } from '../api/tenant'
import type { Session } from '../api/types'
import { Failure } from '../components/Failure'

const SEVERITY: Record<VulnRow['severity'], { word: string; colour: string }> = {
  critical: { word: 'critical', colour: 'var(--sev-critical)' },
  high: { word: 'high', colour: 'var(--sev-high)' },
  medium: { word: 'medium', colour: 'var(--sev-medium)' },
  low: { word: 'low', colour: 'var(--sev-low)' },
  // Unrated is not low. "Nobody has scored this" and "this is not very serious" are
  // different statements, and colouring them the same buries the first.
  unknown: { word: 'unrated', colour: 'var(--sev-info)' },
}

const STATE_WORD: Record<VulnRow['state'], string> = {
  open: 'open',
  no_fix: 'no fix published',
  accepted: 'risk accepted',
  resolved: 'resolved',
}

/**
 * Vulnerabilities — what the estate is exposed to, and what it cannot answer for.
 *
 * The unassessed-hosts panel is not a footnote and is not collapsible. A posture number
 * computed over two thirds of an estate while presenting itself as the whole is the most
 * misleading thing this product could draw, and a host absent from a vulnerability
 * report is indistinguishable from a host with nothing wrong with it. So the hosts that
 * could not be assessed are named, with the reason, above the numbers.
 *
 * `no fix published` is shown as its own state rather than folded into open. There is
 * nothing to upgrade to, which is a different conversation from an outstanding patch —
 * and an "actionable" filter that included it would send somebody looking for a package
 * that does not exist yet.
 */
export function VulnerabilitiesPage({
  tenants = [],
  session,
}: {
  tenants?: string[]
  session?: Session
}) {
  const [tenant] = useTenant(tenants)
  const [severity, setSeverity] = useState('')
  const [state, setState] = useState('')
  const [actionable, setActionable] = useState(false)

  const summary = useVulnSummary(tenant)
  const findings = useVulnerabilities({
    tenant,
    ...(severity ? { severity } : {}),
    ...(state ? { state } : {}),
    ...(actionable ? { actionable: 'true' } : {}),
  })
  const accept = useAcceptVulnerability(tenant)

  const canSuppress = session?.memberships.some(
    (membership) => membership.tenant === tenant && membership.can_suppress,
  )

  if (summary.isError) return <Failure error={summary.error} onRetry={() => summary.refetch()} />

  return (
    <section aria-labelledby="vuln-heading">
      <header
        style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h2 id="vuln-heading" style={{ margin: 0 }}>
          Vulnerabilities
        </h2>
        <span className="muted">
          {summary.data
            ? `${summary.data.advisories_held} advisor${
                summary.data.advisories_held === 1 ? 'y' : 'ies'
              } held · ${summary.data.hosts_assessed} host(s) assessed`
            : 'loading'}
        </span>
      </header>

      {summary.data && summary.data.advisories_held === 0 && (
        <div className="error" role="alert" style={{ marginTop: 'var(--space-4)' }}>
          <strong>No advisories have been imported.</strong> Every host below will read as
          clean, and that is a statement about this database rather than about the estate.
          Import an OSV export with <span className="mono">manage.py import_advisories</span>.
        </div>
      )}

      {summary.data && summary.data.hosts_unassessed.length > 0 && (
        <div className="error" role="alert" style={{ marginTop: 'var(--space-4)' }}>
          <strong>
            {summary.data.hosts_unassessed.length} host(s) could not be assessed, and are
            not counted below
          </strong>
          <ul style={{ marginBottom: 0 }}>
            {summary.data.hosts_unassessed.map((host) => (
              <li key={host.hostname}>
                <span className="mono">{host.hostname}</span> — {host.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary.data && (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
            marginTop: 'var(--space-4)',
          }}
        >
          {(['critical', 'high', 'medium', 'low', 'unknown'] as const).map((band) => (
            <div className="panel" key={band} style={{ minWidth: '9rem' }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--text-lg)',
                  color: SEVERITY[band].colour,
                }}
              >
                {summary.data.by_severity[band] ?? 0}
              </p>
              <p className="muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
                {SEVERITY[band].word}
              </p>
            </div>
          ))}
          <div className="panel" style={{ minWidth: '9rem' }}>
            <p style={{ margin: 0, fontSize: 'var(--text-lg)' }}>
              {summary.data.by_state.no_fix ?? 0}
            </p>
            <p className="muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
              with no fix published
            </p>
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          marginTop: 'var(--space-5)',
        }}
      >
        <label htmlFor="vuln-severity" className="muted">
          Severity
        </label>
        <select
          id="vuln-severity"
          value={severity}
          onChange={(event) => setSeverity(event.target.value)}
        >
          <option value="">any</option>
          <option value="critical">critical</option>
          <option value="high">high</option>
          <option value="medium">medium</option>
          <option value="low">low</option>
          <option value="unknown">unrated</option>
        </select>

        <label htmlFor="vuln-state" className="muted">
          State
        </label>
        <select id="vuln-state" value={state} onChange={(event) => setState(event.target.value)}>
          <option value="">outstanding</option>
          <option value="open">open</option>
          <option value="no_fix">no fix published</option>
          <option value="accepted">risk accepted</option>
          <option value="resolved">resolved</option>
        </select>

        <label>
          <input
            type="checkbox"
            checked={actionable}
            onChange={(event) => setActionable(event.target.checked)}
          />{' '}
          Only what can be upgraded today
        </label>
      </div>

      {findings.isError ? (
        <Failure error={findings.error} onRetry={() => findings.refetch()} />
      ) : !findings.data ? (
        <div aria-hidden="true" style={{ marginTop: 'var(--space-4)' }}>
          {Array.from({ length: 4 }, (_, index) => (
            <div className="skeleton" key={index} />
          ))}
        </div>
      ) : findings.data.results.length === 0 ? (
        <p className="empty">
          Nothing matches. With {summary.data?.advisories_held ?? 0} advisories held and{' '}
          {summary.data?.hosts_assessed ?? 0} host(s) assessed, that is as strong a
          statement as those two numbers allow.
        </p>
      ) : (
        <>
          <p className="muted" style={{ marginTop: 'var(--space-4)' }}>
            {findings.data.count} finding(s), worst first
            {findings.data.count > findings.data.results.length
              ? ` · showing ${findings.data.results.length}`
              : ''}
          </p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {findings.data.results.map((row) => (
              <li key={row.id} style={{ marginBottom: 'var(--space-2)' }}>
                <Finding
                  row={row}
                  canSuppress={Boolean(canSuppress)}
                  onAccept={(reason) => accept.mutate({ id: row.id, reason })}
                  pending={accept.isPending}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

function Finding({
  row,
  canSuppress,
  onAccept,
  pending,
}: {
  row: VulnRow
  canSuppress: boolean
  onAccept: (reason: string) => void
  pending: boolean
}) {
  const [reason, setReason] = useState('')
  const [asking, setAsking] = useState(false)
  const severity = SEVERITY[row.severity]

  return (
    <div
      className="panel"
      style={{ borderLeft: `3px solid ${severity.colour}`, padding: 'var(--space-3)' }}
    >
      <div
        style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'baseline' }}
      >
        <span style={{ color: severity.colour, fontSize: 'var(--text-xs)' }}>
          {severity.word}
          {row.cvss_score > 0 ? ` ${row.cvss_score.toFixed(1)}` : ''}
        </span>
        <span className="mono">{row.cve || row.osv_id}</span>
        <span className="mono">{row.hostname}</span>
        <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          {STATE_WORD[row.state]}
        </span>
        {canSuppress && row.state !== 'resolved' && row.state !== 'accepted' && (
          <button style={{ marginLeft: 'auto' }} onClick={() => setAsking((open) => !open)}>
            Accept this risk
          </button>
        )}
      </div>

      <p className="mono" style={{ margin: 'var(--space-2) 0 0' }}>
        {row.package} {row.installed_version}
        {row.fixed_version ? ` → ${row.fixed_version}` : ''}
      </p>
      {!row.fixed_version && (
        <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--warn)' }}>
          No fix has been published for this ecosystem — there is nothing to upgrade to yet.
        </p>
      )}
      {row.summary && (
        <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--text-xs)' }}>{row.summary}</p>
      )}
      {row.assessed_as && (
        <p className="muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
          {/* A derivative assessed against its parent is a weaker claim, and the reader
              has to be able to tell which one they are looking at. */}
          this host runs a derivative and was assessed against {row.assessed_as}
        </p>
      )}
      {row.state === 'accepted' && (
        <p className="muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
          accepted by {row.accepted_by}: {row.accepted_reason}
        </p>
      )}
      {row.state === 'resolved' && (
        <p className="muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
          resolved{row.resolved_version ? ` by ${row.resolved_version}` : ''}
          {row.resolved_at ? ` on ${new Date(row.resolved_at).toLocaleDateString()}` : ''}
        </p>
      )}

      {asking && (
        <form
          style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 'var(--space-2)' }}
          onSubmit={(event) => {
            event.preventDefault()
            if (reason.trim()) onAccept(reason.trim())
          }}
        >
          <label htmlFor={`reason-${row.id}`} className="visually-hidden">
            Why this risk is being accepted
          </label>
          <input
            id={`reason-${row.id}`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="not reachable from outside; next maintenance window"
            style={{ flex: 1 }}
          />
          {/* The reason is required by the API and required here, for the same reason:
              an accepted vulnerability with no stated reason is indistinguishable from
              one somebody clicked past. */}
          <button type="submit" disabled={pending || !reason.trim()}>
            Record
          </button>
        </form>
      )}
    </div>
  )
}
